import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, OnDestroy} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {catchError, finalize, of, Subject, Subscription, takeUntil, timeout, timer} from 'rxjs';
import {MaterialModule} from '../../../material.module';
import {ClientService} from '../../../services/auth/client.service';
import {
    EnabledProductInfo,
    ModuleAccessInfo,
    ShineUserInfoService,
    UserManagementUserRow,
    UserManagementUsersResponse
} from '../../../services/auth/shine-auth/shine-user-info.service';
import {
    readUserManagementVisualFixture,
    UserManagementVisualFixtureState
} from './user-management-visual-fixture.guard';

type PageState = 'loading' | 'ready' | 'empty' | 'error' | 'unauthorized';
type AccessEntry = EnabledProductInfo | ModuleAccessInfo;

interface UserManagementUserView {
    id: string;
    email: string;
    access: string;
    enabledProducts: EnabledProductInfo[];
    moduleAccess: ModuleAccessInfo[];
}

@Component({
    selector: 'app-user-management',
    standalone: true,
    imports: [CommonModule, MaterialModule],
    templateUrl: './user-management.component.html',
    styleUrls: ['../../connect/connect-shared.scss', './user-management.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {class: 'connect-theme'}
})
export class UserManagementComponent implements OnInit, OnDestroy {
    private readonly userInfoService = inject(ShineUserInfoService);
    private readonly clientService = inject(ClientService);
    private readonly cdr = inject(ChangeDetectorRef);

    private readonly destroyed = new Subject<void>();
    private polling: Subscription | null = null;
    private requestPending = false;

    state: PageState = 'loading';
    rightsVersion: number | null = null;
    users: UserManagementUserView[] = [];
    selectedClient = {
        id: '',
        name: '',
        domain: ''
    };

    ngOnInit(): void {
        const fixtureState = this.getFixtureState();
        if (fixtureState) {
            this.applyFixture(fixtureState);
            return;
        }

        this.loadUsers();
        this.polling = timer(30_000, 30_000).subscribe(() => {
            if (document.visibilityState !== 'hidden') this.loadUsers(true);
        });
    }

    ngOnDestroy(): void {
        this.polling?.unsubscribe();
        this.destroyed.next();
        this.destroyed.complete();
    }

    loadUsers(background: boolean = false): void {
        if (this.requestPending) return;
        const selectedClientId = this.getSelectedClientId();
        if (!selectedClientId) {
            this.state = 'error';
            this.users = [];
            this.cdr.markForCheck();
            return;
        }

        if (!background) this.state = 'loading';
        this.requestPending = true;
        this.userInfoService.getClientUsers(selectedClientId)
            .pipe(
                timeout(10_000),
                takeUntil(this.destroyed),
                catchError(error => {
                    this.state = this.isUnauthorized(error) ? 'unauthorized' : 'error';
                    this.users = [];
                    this.cdr.markForCheck();
                    return of<UserManagementUsersResponse | null>(null);
                }),
                finalize(() => {
                    this.requestPending = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe(response => {
                if (!response || selectedClientId !== this.getSelectedClientId()) return;
                if (response.status !== 'success') {
                    this.state = 'error';
                    this.users = [];
                    return;
                }

                this.applyResponse(response);
            });
    }

    accessBadge(access: string | null | undefined): {label: string; className: string} {
        const normalized = this.normalizeAccess(access);
        switch (normalized) {
            case 'admin':
                return {label: 'Admin', className: 'badge green'};
            case 'user':
                return {label: 'User', className: 'badge blue'};
            case 'read-only':
                return {label: 'Read only', className: 'badge gray'};
            default:
                return {label: normalized || 'Denied', className: 'badge gray'};
        }
    }

    trackUser(_index: number, user: UserManagementUserView): string {
        return user.id || user.email;
    }

    trackAccess(_index: number, access: AccessEntry): string {
        return `${access.name}:${access.access}`;
    }

    get totalModuleCount(): number {
        return this.users.reduce((sum, user) => sum + user.moduleAccess.length, 0);
    }

    get adminUserCount(): number {
        return this.users.filter(user =>
            this.normalizeAccess(user.access) === 'admin'
        ).length;
    }

    private applyResponse(response: UserManagementUsersResponse): void {
        this.rightsVersion = response.data?.rightsVersion ?? null;
        this.selectedClient = {
            id: response.data?.client?.id || this.getSelectedClientId(),
            name: response.data?.client?.Name || this.clientService.client?.name || this.getSelectedClientId(),
            domain: response.data?.client?.Domain || ''
        };
        this.users = (response.data?.users ?? [])
            .map(user => this.mapUser(user))
            .filter((user): user is UserManagementUserView => user !== null);
        this.state = this.users.length === 0 ? 'empty' : 'ready';
    }

    private mapUser(user: UserManagementUserRow): UserManagementUserView | null {
        const id = user.id || user.email;
        if (!id) return null;
        return {
            id,
            email: user.email || id,
            access: user.access || '',
            enabledProducts: user.enabledProducts ?? [],
            moduleAccess: user.moduleAccess ?? []
        };
    }

    private normalizeAccess(access: string | null | undefined): string {
        return (access ?? '').trim().toLowerCase();
    }

    private isUnauthorized(error: unknown): boolean {
        return error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
    }

    private getFixtureState(): UserManagementVisualFixtureState | null {
        if (typeof window === 'undefined' || !window.location) return null;
        return readUserManagementVisualFixture(window.location.href);
    }

    private applyFixture(state: UserManagementVisualFixtureState): void {
        if (state === 'loading') {
            this.state = 'loading';
            return;
        }

        if (state === 'error' || state === 'unauthorized') {
            this.state = state;
            return;
        }

        this.rightsVersion = 1;
        this.selectedClient = {
            id: 'company-fixture-1',
            name: 'Fjord Mobility',
            domain: 'fjord.example'
        };
        this.users = state === 'empty' ? [] : [
            {
                id: 'contact-fixture-admin',
                email: 'admin@example.test',
                access: 'admin',
                enabledProducts: [
                    {name: 'Shine AI', access: 'admin'},
                    {name: 'Shine Enrich', access: 'admin'}
                ],
                moduleAccess: [
                    {name: 'Shine AI', access: 'admin'},
                    {name: 'Shine Enrich', access: 'admin'}
                ]
            },
            {
                id: 'contact-fixture-user',
                email: 'user@example.test',
                access: 'user',
                enabledProducts: [
                    {name: 'Shine AI', access: 'user'},
                    {name: 'Shine Enrich', access: 'user'}
                ],
                moduleAccess: [
                    {name: 'Shine AI', access: 'user'},
                    {name: 'Shine Enrich', access: 'user'}
                ]
            }
        ];
        this.state = this.users.length === 0 ? 'empty' : 'ready';
    }

    private getSelectedClientId(): string {
        const client = this.clientService.client;
        return client?.id || client?.domain || client?.name || '';
    }
}
